import React, { useEffect, useState } from "react";
import NameDialog from "../layout/app/NameDialog";
import { useToggleTop } from "../common/ToggleTop";
import { useUser } from "../../providers/UserProvider";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { apiMessage } from "../../api/apiMessage";
import { nameTaken, placesChanged } from "./places";

const WORDS = {
  home: { title: "Create Home", label: "Home name", noun: "Home" },
  floor: { title: "Create Floor", label: "Floor name", noun: "Floor" },
  room: { title: "Create Room", label: "Room name", noun: "Room" },
};

// Creates a home, or a floor or room in the selected home, as the app's
// AddHome, AddFloor and AddRoom do. A room goes on a floor, which the API
// names it by; floorName picks one to start with.
const CreatePlaceDialog = ({ kind, open, onClose, floorName }) => {
  const toast = useToggleTop();
  const { selectedPropertey } = useUser();
  const [busy, setBusy] = useState(false);
  const [floors, setFloors] = useState(null);
  const [floor, setFloor] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const propertyId = User.getPropertyId();
  const words = WORDS[kind];

  useEffect(() => {
    if (!open || kind !== "room") return undefined;
    let current = true;
    setFloors(null);
    setLoadFailed(false);
    UserService.getRooms(propertyId)
      .then((result) => {
        if (!current) return;
        const names = (result?.floors ?? []).map((item) => item.name);
        setFloors(names);
        setFloor(names.includes(floorName) ? floorName : names[0] ?? "");
      })
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
  }, [open, kind, propertyId, floorName]);

  const create = async (name) => {
    setBusy(true);
    try {
      let response;
      if (kind === "home") response = await UserService.createProperty({ name });
      else if (kind === "floor") response = await UserService.createFloor(propertyId, { name });
      else response = await UserService.createRoom(propertyId, { floor, name });
      const taken = nameTaken(response);
      if (taken) {
        toast(taken, "red");
        return;
      }
      toast(`${words.noun} "${name}" created`, "green");
      onClose();
      if (kind === "home") await selectedPropertey();
      placesChanged();
    } catch (error) {
      toast(apiMessage(error, `Couldn't create the ${kind}. Please try again.`), "red");
    } finally {
      setBusy(false);
    }
  };

  const noFloors = kind === "room" && floors !== null && !floors.length;

  return (
    <NameDialog
      open={open}
      idPrefix={`hx-create-${kind}`}
      title={words.title}
      label={words.label}
      submitLabel="Add"
      busy={busy}
      disabled={kind === "room" && !floor}
      onClose={onClose}
      onSubmit={create}
    >
      {kind === "room" ? (
        loadFailed ? (
          <p className="hx-name-note is-error">Couldn't load this home's floors. Please try again.</p>
        ) : floors === null ? (
          <p className="hx-name-note">Loading floors…</p>
        ) : noFloors ? (
          <p className="hx-name-note">This home has no floors yet. Create a floor first.</p>
        ) : (
          <>
            <label className="hx-label" htmlFor="hx-create-room-floor">
              Floor
            </label>
            <select
              id="hx-create-room-floor"
              className="hx-input"
              value={floor}
              onChange={(event) => setFloor(event.target.value)}
            >
              {floors.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </>
        )
      ) : null}
    </NameDialog>
  );
};

export default CreatePlaceDialog;
